import { useHistory, useParams } from "react-router";
import { debounce, isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Form } from "react-final-form";

import {
  isCreateMode,
  isConsultMode,
  isUpdateMode,
  isNoneMode,
  isBackgroundCreateMode,
} from '../../../../utils/store/store-utils';
import QCXMasterWizardFinalForm from "../../../../components/master/QCXMasterWizardFinalForm";
import {
  changeToConsultMode,
  selectMaster,
  selectMode,
  setModel,
  resetModel,
  selectBackgroundMode,
  setResponse,
  success,
  failure,
  setError,
  fetchByIdAsync,
  loading,
  changeToUpdateMode,
  changeToCreateMode,
  addToList,
  selectMasterType,
  selectOperationMaster,
} from "../../../../features/master/masterSlice";
import { register, save } from '../../../../features/master/masterAPI';
import QCXMainLayout from "../../../../components/main-layout/QCXMainLayout";
import { getExisteMasterOrBl, getNovoMasterOrBl } from "../../../../features/master/masterUtils";
import { normalizeNumeral } from "../../../../utils/general/general-utils";

export default function MasterRegistrationPage({ authInfo = {} }) {

  const { t } = useTranslation();

  const { id } = useParams();

  const dispatch = useDispatch();

  const operation = useSelector(selectOperationMaster);

  const history = useHistory();

  const master = useSelector(selectMaster);

  const mode = useSelector(selectMode);

  const backgroundMode = useSelector(selectBackgroundMode);

  const type = useSelector(selectMasterType);

  const isNone = useMemo(() => (
    isNoneMode(mode)
  ), [mode]);

  const isCreate = useMemo(() => (
    isCreateMode(mode)
  ), [mode]);

  const isConsult = useMemo(() => (
    isConsultMode(mode)
  ), [mode]);

  const isUpdate = useMemo(() => (
    isUpdateMode(mode)
  ), [mode]);

  const actionName = useMemo(() => {
    if (isCreate || isNone) {
      return t('com.muralis.qcx.acoes.nova');
    }
    if (isConsult) {
      return t('com.muralis.qcx.acoes.visualizar');
    }
    return t('com.muralis.qcx.acoes.alterar');
  }, [isNone, isCreate, isConsult]);

  const pageTitle = useMemo(() => (
    isCreate
      ? getNovoMasterOrBl(type)
      : getExisteMasterOrBl(type, master?.numeroMaster)
  )
    , [isNone, isCreate, master]);

  const breadcrumbs = useMemo(() => ([
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      text: {
        name: actionName,
      },
    },
  ]), [actionName]);

  useEffect(() => {
    if (!isEmpty(id)) {
      dispatch(fetchByIdAsync(id));
      handleChangeToConsult();
    }
  }, [id]);

  const [tipoFrete, setTipoFrete] = useState("PREPAID");

  const [tipoPagamento, setTipoPagamento] = useState(null);

  const [declarado, setDeclarado] = useState(null)

  const [tipoEmbarque, setTipoEmbarque] = useState("BACK_TO_BACK");

  const [masterCustos, setMasterCustos] = useState([]);

  const unnormalize = useCallback((data) => {
    const unnormalizedData = {
      ...data,
    };

    return unnormalizedData;
  });

  const isDeclarado = (str) => str === "SIM";

  const fetchById = useCallback((masterID) => (
    dispatch(fetchByIdAsync(masterID))
  ), []);

  const getId = (v) => (v && typeof v === 'object' ? (v.id ?? null) : null);

  const normalize = useCallback((data) => {
    const custos = (masterCustos || []).map((x) => {
      const despesaReceitaId =
        getId(x?.despesaReceita) ??
        getId(x?.despesaReceita?.despesaReceita) ??
        getId(x?.ignorableFields?.referenciaCliente?.despesaReceita);

      const moedaId =
        getId(x?.moeda) ??
        getId(x?.moeda?.moeda) ??
        getId(x?.ignorableFields?.referenciaCliente?.moeda);

      return {
        id: null,
        despesaReceita: { id: despesaReceitaId },
        moeda: { id: moedaId },
        tipoPagamento: x?.tipoPagamento ?? "",
        valorCompraMoeda: x?.valorComprar ?? normalizeNumeral(x?.valorCompraMoeda) ?? 0,
        valorCompraReais: x?.valorComprar ?? normalizeNumeral(x?.valorCompraReais) ?? 0,
        valorCompraDolar: x?.valorComprar ?? normalizeNumeral(x?.valorCompraDolar) ?? 0,
        declarado: isDeclarado(x?.declarado),
      };
    });

    return {
      id: data?.id ?? null,
      code: data?.code ?? null,
      description: data?.description ?? null,
      numeroMaster: data?.numeroMaster ?? "",
      consignatario: { id: data?.consignatario?.id ?? null },
      exportador: { id: data?.exportador?.id ?? null },
      transportadorInternacional: { id: data?.transportadorInternacional?.id ?? null },
      origem: { id: data?.origem?.id ?? null },
      destino: { id: data?.destino?.id ?? null },

      tipoFrete: data?.tipoFrete ?? "",
      tipoEmbarque: data?.tipoEmbarque ?? null,

      masterCustos: custos,
      operacao: operation,
    };
  }, [masterCustos, operation]);


  const isBackgroundCreate = useMemo(() => isBackgroundCreateMode(backgroundMode), [backgroundMode]);

  const handleDispatchSetModel = useCallback((data) => {
    dispatch(setModel(unnormalize(data)));
  }, [unnormalize])

  const handleSubmit = async (data, step, next) => {

    if (isUpdate) {

      const normalizedData = normalize(data);

      await update(normalizedData);

    } else {
      next();
    }

  }

  const handleChangeToUpdate = () => {
    dispatch(changeToUpdateMode());
  };

  const handleCancelUpdate = () => {
    if (id) {
      fetchById(id);
    }
    handleChangeToConsult();
  };

  const handleChangeToCreate = () => {
    dispatch(changeToCreateMode());
  };

  const handleChangeToConsult = () => {
    dispatch(changeToConsultMode());
  };

  const handleChangeToBackgroundCreate = () => { };

  const handleResetBackgroundMode = () => { };

  const handleAlternativeSave = async (event, step, next) => {

    event.stopPropagation();

    const normalizedData = normalize(master);

    if (isCreate) {
      await create(normalizedData);
    }

    if (isUpdate) {
      await update(normalizedData);
    }

  }

  const model = useMemo(() => unnormalize(master), [master, unnormalize]);

  const create = async (data) => {

    const executeDebounced = debounce(async () => {

      try {

        const response = await register(data);

        if (response?.status === 201) {

          dispatch(resetModel());

          const created = response?.data;

          handleDispatchSetModel(created);

          dispatch(addToList({ data: created }));

          const handleResultWithDebounce = debounce(() => {

            history.push(t('com.muralis.qcx.url.moduloAgenciamentoMaster'))

            dispatch(success());

            dispatch(setResponse({
              status: response.status,
              data: created,
              message: t('com.muralis.qcx.mensagem.masterCadastrada'),
            }));

          }, 500)

          handleResultWithDebounce();
        }
      } catch (error) {

        const defaultErrorMessage = t('com.muralis.qcx.erro.erroCadastrarMaster', { descricao: error?.response?.data?.message });

        const errorMessage = error?.response?.data?.message
          ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
          : `${defaultErrorMessage}.`;


        dispatch(failure());

        dispatch(setError({
          message: errorMessage,
        }));

      }
    }, 500)

    dispatch(loading());
    executeDebounced();
  }

  const update = async (data) => {
    const executeDebounced = debounce(async () => {

      try {

        const response = await save(data);

        if (response?.status === 200) {

          dispatch(resetModel());

          const created = response?.data;

          handleDispatchSetModel(created);

          dispatch(addToList({ data: created }));

          const handleResultWithDebounce = debounce(() => {

            history.push(t('com.muralis.qcx.url.moduloAgenciamentoMaster'))

            dispatch(success());

            dispatch(setResponse({
              status: response.status,
              data: created,
              message: t('com.muralis.qcx.mensagem.masterSalvar', { descricao: created?.numeroMaster }),
            }));

          }, 500)

          handleResultWithDebounce();
        }
      } catch (error) {

        const defaultErrorMessage = t('com.muralis.qcx.erro.erroCadastrarMaster', { descricao: error?.response?.data?.message });

        const errorMessage = error?.response?.data?.message
          ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
          : `${defaultErrorMessage}.`;


        dispatch(failure());

        dispatch(setError({
          message: errorMessage,
        }));

      }
    }, 500)

    dispatch(loading());
    executeDebounced();
  }

  return (
    <QCXMainLayout pageTitle={pageTitle} breadcrumbs={breadcrumbs} authInfo={authInfo}>
      <Container style={{ display: 'flex', padding: '0 1rem 3rem 1rem' }}>
        <QCXMasterWizardFinalForm
          isCreate={isCreate}
          isConsult={isConsult}
          isUpdate={isUpdate}
          isBackgroundCreate={isBackgroundCreate}
          model={model}
          handleChangeModel={handleDispatchSetModel}
          handleSubmit={handleSubmit}
          handleChangeToCreate={handleChangeToCreate}
          handleChangeToConsult={handleChangeToConsult}
          handleAlternativeSave={handleAlternativeSave}
          handleChangeToUpdate={handleChangeToUpdate}
          handleCancelUpdate={handleCancelUpdate}
          handleChangeToBackgroundCreate={handleChangeToBackgroundCreate}
          handleResetBackgroundMode={handleResetBackgroundMode}
          authInfo={authInfo}
          tipoFrete={tipoFrete}
          setTipoFrete={setTipoFrete}
          tipoEmbarque={tipoEmbarque}
          setTipoEmbarque={setTipoEmbarque}
          requiredRoles={['cliente']}
          tipoPagamento={tipoPagamento}
          setTipoPagamento={setTipoPagamento}
          declarado={declarado}
          setDeclarado={setDeclarado}
          setMasterCustos={setMasterCustos}
        />
      </Container>
    </QCXMainLayout>
  )
}